/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import range from 'lodash.range';
import { useLocation } from 'react-router';

import { IPagination } from '@/commons/commons';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@/components/ui/pagination';

const pageRange = (from: number, to: number) => range(from, to + 1, 1);

interface PaginationProps extends IPagination {
    onPageChange?: (page: number) => void;
}

const DefaultPagination = ({
    currentPage,
    lastPage: totalPages,
    prevPage,
    nextPage,
    onPageChange,
}: PaginationProps) => {
    const location = useLocation();

    const paginationLimit = 3;
    const minTotalPagesToCollapse = 4;
    const maxAdjacentPages = Math.max(Math.ceil((paginationLimit - 1) / 2), 0);

    const linkGen = (page: number = 1) => {
        if (onPageChange) {
            return '#';
        }
        const newSearchParams = new URLSearchParams();
        newSearchParams.set('page', page.toString());
        return `${location.pathname}?${newSearchParams.toString()}`;
    };

    const handlePageChange = (page: number) => {
        if (onPageChange) {
            onPageChange(page);
        }
    };

    const renderPageButtons = () => {
        if (totalPages < 1) return null;
        let items = [];
        const getPageItem = (page: number) => {
            const isActivePage = page === currentPage;
            return (
                <PaginationItem key={page}>
                    <PaginationLink
                        href={linkGen(page)}
                        isActive={isActivePage}
                        onClick={(e) => {
                            if (onPageChange) {
                                e.preventDefault();
                                handlePageChange(page);
                            }
                        }}
                    >
                        {page}
                    </PaginationLink>
                </PaginationItem>
            );
        };
        const getEllipsisItem = (side: 'left' | 'right') => (
            <PaginationItem key={`ellipsis-${side}`}>
                <PaginationEllipsis />
            </PaginationItem>
        );
        const shouldCollapseLeftSide = () => {
            const diff = currentPage - maxAdjacentPages;
            // Magic 3: prevents collapsing a single page on the left side
            return diff >= maxAdjacentPages && diff > 3 && totalPages > minTotalPagesToCollapse;
        };
        const shouldCollapseRightSide = () => {
            // Magic 2: prevents collapsing a single page on the right side
            const diff = totalPages - 2 - currentPage;
            return diff > maxAdjacentPages && totalPages > minTotalPagesToCollapse;
        };
        const collapseLeftSide = shouldCollapseLeftSide();
        const collapseRightSide = shouldCollapseRightSide();
        let firstPage = collapseLeftSide ? currentPage - maxAdjacentPages : 1;
        // If we're on last page, show at least one page to the left
        firstPage = Math.min(firstPage, Math.max(totalPages - 1, 1));
        let lastPage = collapseRightSide ? currentPage + maxAdjacentPages : totalPages;
        // If we're on first page, show at least one page to the right
        lastPage = Math.max(lastPage, Math.min(totalPages, 2));
        // Default numbered items
        items = pageRange(firstPage, lastPage).map((page) => getPageItem(page));
        if (collapseLeftSide) {
            items.splice(0, 0, getPageItem(1), getEllipsisItem('left'));
        }
        if (collapseRightSide) {
            items.push(getEllipsisItem('right'), getPageItem(totalPages));
        }
        return items;
    };

    return (
        <Pagination className="py-4">
            <PaginationContent>
                {prevPage && (
                    <PaginationItem>
                        <PaginationPrevious
                            href={linkGen(prevPage)}
                            onClick={(e) => {
                                if (onPageChange) {
                                    e.preventDefault();
                                    handlePageChange(prevPage);
                                }
                            }}
                        />
                    </PaginationItem>
                )}
                {renderPageButtons()}
                {nextPage && (
                    <PaginationItem>
                        <PaginationNext
                            href={linkGen(nextPage)}
                            onClick={(e) => {
                                if (onPageChange) {
                                    e.preventDefault();
                                    handlePageChange(nextPage);
                                }
                            }}
                        />
                    </PaginationItem>
                )}
            </PaginationContent>
        </Pagination>
    );
};

export default DefaultPagination;
